/**
 * roomSource.js — one room report, however the room arrived.
 *
 * 🔴 THE SOURCE OF A ROOM IS NOT A REASON FOR A SECOND PAGE. Ben, Sep 2 2026,
 * after walking the pasted-room flow: the two league reports had drifted into
 * two products — one ranked expandable cards with best buy, worst overpay and
 * the room's headline sale, the other a thinner list with none of it. A room
 * fetched from Sleeper and a room read off a Yahoo results page are the same
 * object with different provenance, so this module turns each of them into ONE
 * shape and RoomReport.jsx draws that shape and nothing else.
 *
 * WHAT AN ADAPTER MAY DO: rename, rank, project, and compute a number both
 * sides can compute from what they already hold.
 * WHAT AN ADAPTER MUST NEVER DO: invent a field the other side would have to
 * fake. A fetched Sleeper auction knows things a paste cannot, and where that
 * is true the field arrives null and the page draws nothing. The list of such
 * fields is short and it is written down in the header of RoomReport.jsx.
 *
 * 🔴 ONE PAGE, ONE YARDSTICK, AND THAT SURVIVES THE MERGE. Every badge this
 * module emits is ROOM-relative: the letter is surplus rank inside this
 * auction, the position words are terciles over these teams. The corpus
 * percentiles belong to a SEAT's own report (TeamReportView) and never appear
 * here. That was already the rule on both pages separately; it is now the rule
 * in one place.
 */

import { buildArchetype } from "./buildArchetype.js";
import { roomMarketClaim } from "./roomSizeHeat.js";
import { isQbPremiumFormat } from "./leagueFormat.js";
import { roomMedianShares } from "./leagueSpend.js";
import { signedMoney } from "./money.js";
import { assignPositionGrades, projectRoom } from "./rateProjection.js";
import { positionRanks, projectionSlots, roomGradesBySurplus } from "./rateTeam.js";
import { roomRowOrder } from "./roomOrder.js";
import { ordinal } from "./ordinal.js";
import { scoringFromSleeper } from "./sleeperImport.js";

/**
 * How hot the room ran against the real market, as a percent.
 *
 * The SAME estimator api/_lib/rateReport.js uses, deliberately: only picks the
 * benchmark table actually carries (`source === "player"`) count, on both
 * sides, so "the real market" stays literal and a ladder fallback never
 * appears in a sentence about what real rooms paid.
 *
 * Null when no pick in the room has a real benchmark — a room with no priced
 * picks, or one whose players the table does not carry — because the headline
 * has to be a fact or absent.
 */
export function roomHeatOf(picks) {
  let paid = 0;
  let bench = 0;
  for (const p of picks || []) {
    if (p.source !== "player") continue;
    paid += p.paid || 0;
    bench += p.bench || 0;
  }
  if (bench <= 0) return null;
  return Math.round((paid / bench - 1) * 1000) / 10;
}

/**
 * How many sales each superlative board shows at most.
 *
 * Five is a board a reader takes in at a glance and still long enough that the
 * room's shape shows rather than just its one loudest sale. Exported so the view
 * and its tests cannot disagree about the number.
 */
export const TOP_SALES = 5;

/**
 * The room's five best buys and five worst overpays, with the seat that made
 * each one.
 *
 * 🔴 THE SAME PER-PICK SURPLUS THE HEADLINE SALES ALREADY USE, and the same
 * population: `source === "player"`, so only picks the benchmark table really
 * carries are eligible and a ladder fallback never appears in a sentence about
 * what the market paid. The head of `steals` is therefore the room's biggest
 * steal by construction — the board and the headline above it cannot disagree.
 *
 * RANKED ON THE DOLLAR GAP, not on a percentage. A $2 flier bought for $1 is a
 * 50% steal and nobody's story; the surplus is the story, and it is already in
 * the league's own dollars. Ties break on the pricier player — the bigger sale
 * is the bigger headline — and then on the name, so the order is total and a
 * re-render cannot reshuffle the board.
 *
 * FEWER THAN FIVE QUALIFYING SALES SHOWS WHAT EXISTS. A steal has to be a
 * steal (surplus > 0) and an overpay an overpay (surplus < 0), so a room where
 * everything cleared near the market simply has a shorter board, never a padded
 * one carrying a "+$0 steal".
 *
 * @param {Array} cards the room's team cards: each { name, picks }
 */
export function roomSuperlatives(cards, limit = TOP_SALES) {
  const priced = [];
  for (const c of cards || []) {
    for (const p of c.picks || []) {
      if (p.source !== "player") continue;
      priced.push({
        player: p.player,
        pos: p.pos,
        team: c.name,
        paid: p.paid,
        bench: p.bench,
        surplus: p.surplus,
      });
    }
  }
  const board = (dir) =>
    priced
      .filter((p) => (dir > 0 ? p.surplus > 0 : p.surplus < 0))
      .sort(
        (a, b) =>
          (b.surplus - a.surplus) * dir ||
          (b.bench || 0) - (a.bench || 0) ||
          a.player.localeCompare(b.player)
      )
      .slice(0, limit);
  return { steals: board(1), overpays: board(-1) };
}

/**
 * The band, in the league's own dollars, inside which a room counts as having
 * paid what the market says rather than over or under it.
 *
 * The same half-dollar the team rows already colour on (TeamListRow's
 * surplusColorOf), so a room the page paints as level cannot be described as
 * "under" in the sentence above it.
 */
export const ROOM_LEVEL_BAND = 0.5;

/**
 * Why nine of ten teams can show a green number, said once, and the room
 * average every team's own line is then measured against.
 *
 * 🔴 THE ARITHMETIC WAS ALREADY ON THE PAGE AND NOBODY WAS DOING IT. Ben, on
 * the deployed report: nine of ten teams show a positive surplus. They do, and
 * it is not a bug — a room that paid under the market has to hand out mostly
 * positive surpluses, because the surpluses ARE that underpayment split ten
 * ways. What was missing is the sentence connecting the two, so a reader with a
 * green +$26 and a C had to work out for themselves that the letter ranks them
 * inside the room and the dollars do not.
 *
 * 🔴 IT IS COMPUTED FROM THE CARDS, NOT FROM THE HEAT. `roomHeatOf` counts only
 * `source === "player"` picks; a team's surplus counts all of them. Building
 * this sentence out of the heat would let it say "under" over a set of cards
 * that sum the other way. So the direction and the total come from the SAME
 * numbers the cards below print, which also means a reader can add up the
 * column and get the figure in the sentence.
 *
 * 🔴 THE LETTERS ARE ONLY MENTIONED WHEN THERE ARE LETTERS. A room one seat of
 * which was typed without dollars carries no grades and no room-wide market
 * comparison, so it gets the average and no sentence — never a note explaining a
 * ranking the page is not making.
 *
 * @param {Array} cards the room's team cards
 * @returns {{avgSurplus: number|null, surplusNote: string|null}}
 */
export function roomSurplusContext(cards) {
  const priced = (cards || []).filter((c) => c.hasPrices && Number.isFinite(c.surplus));
  if (priced.length === 0) return { avgSurplus: null, surplusNote: null };
  const total = priced.reduce((a, c) => a + c.surplus, 0);
  const avgSurplus = Math.round((total / priced.length) * 10) / 10;

  const graded = priced.length === cards.length && cards.every((c) => c.badgeIsGrade);
  // One team is not a room, and "1 of 1 teams shows a positive surplus" is a
  // sentence about nothing.
  if (!graded || priced.length < 2) return { avgSurplus, surplusNote: null };

  const positive = priced.filter((c) => c.surplus > 0).length;
  // 🔴 THE COUNT IS A FACT AND NEVER A CLAIM. "Most teams show a positive
  // surplus" is the sentence a reader wants and it is not always true — one big
  // steal can drag a room's total under the market while seven seats sit red —
  // so the count is printed rather than characterised. The "so" attaches to the
  // AVERAGE, which follows from the total by arithmetic and cannot be wrong.
  // The verb agrees with the COUNT and the noun with the total, which is always
  // plural here because a one-team room is turned away above: "1 of the 3 teams
  // shows", "9 of the 10 teams show".
  const shows = positive === 1 ? "shows" : "show";
  const ranking = "The letter grades rank teams against each other, not against zero.";
  if (Math.abs(avgSurplus) <= ROOM_LEVEL_BAND) {
    return {
      avgSurplus,
      surplusNote:
        `This room paid about what the pooled market says overall, so the surpluses here split ` +
        `around zero: ${positive} of the ${priced.length} teams ${shows} a positive number. ${ranking}`,
    };
  }
  const word = avgSurplus > 0 ? "under" : "over";
  return {
    avgSurplus,
    surplusNote:
      `This room paid $${Math.round(Math.abs(total))} ${word} the pooled market overall, so the ` +
      `average surplus here is ${signedMoney(avgSurplus)} and ${positive} of the ` +
      `${priced.length} teams ${shows} a positive number. ${ranking}`,
  };
}

/** The words a room gets when no market percentage can be claimed for it. */
export const ROOM_RELATIVE_HEADLINE = "your whole room, graded against itself";

/**
 * The one sentence at the top, and the line under it.
 *
 * 🔴 ONE CALL, TWO FIELDS. The headline and its note are two halves of one
 * claim and are computed together here, then carried on the room shape. The
 * page renders them; it does not re-derive either, because deriving the second
 * from the first is how the two would come to disagree.
 *
 * 🔴 AND THE PERCENTAGE IS NOT ALWAYS MADE. roomMarketClaim returns a null
 * headline for a room whose size the corpus cannot back, and the fallback is
 * the room-relative sentence — the same one an unpriced room already gets. See
 * roomSizeHeat.js for why a thin size cell forfeits the claim rather than
 * borrowing the pooled one.
 */
function roomClaim(benchmarkFormat, teams, heat) {
  const claim = roomMarketClaim({ benchmarkFormat, teams, roomHeat: heat });
  return { headline: claim.headline || ROOM_RELATIVE_HEADLINE, sizeNote: claim.note };
}

/**
 * Sleeper's roster_positions counts -> the house position shape rateTeam reads.
 *
 * The only real rename is SUPER_FLEX -> SFLEX. The three receiver/back flex
 * spellings Sleeper ships all collapse into FLEX because that is the only flex
 * our lineup filler models; a REC_FLEX room therefore gets a slightly more
 * permissive flex than it plays, which can only ever move a projected lineup,
 * never a price. Bench rides along so the roster total matches the league.
 */
export function houseSlotsFromSleeper(positions) {
  const p = positions || {};
  const n = (k) => Number(p[k]) || 0;
  return {
    QB: n("QB"),
    RB: n("RB"),
    WR: n("WR"),
    TE: n("TE"),
    FLEX: n("FLEX") + n("REC_FLEX") + n("WRRB_FLEX"),
    SFLEX: n("SUPER_FLEX"),
    DEF: n("DEF"),
    K: n("K"),
    BN: n("BN"),
  };
}

/**
 * The league settings ONE seat of a fetched Sleeper room is graded under.
 *
 * 🔴 THIS IS WHAT MAKES THE SLEEPER PATH'S CLICK-THROUGH REAL RATHER THAN A
 * SECOND ROOM PAGE. rateTeam grades a lone team against the corpus, and it
 * needs a house-shaped league; a Sleeper league speaks slot names and a raw
 * scoring_settings block. Both conversions already existed for other reasons
 * (houseSlotsFromSleeper above, scoringFromSleeper for the position board) and
 * this is the one place they are put together.
 *
 * Scoring is null when Sleeper sent none, which rateTeam already handles: the
 * display projection is omitted and every percentile still stands, because the
 * corpus half is scored under the reference lens either way.
 */
export function seatSettingsFromSleeper(report, scoring, leagueName) {
  return {
    name: leagueName || null,
    teams: report.teams,
    budget: report.budget,
    positions: houseSlotsFromSleeper(report.positions),
    scoring: scoring ? scoringFromSleeper(scoring) : null,
  };
}

/**
 * One Sleeper seat's picks as ladder entries.
 *
 * Identity fields ride along so the seat's own report can draw the same
 * headshots the room page does. Price is the league's own dollars, which is
 * what rateTeam expects.
 */
export function seatEntriesFromSleeper(team) {
  return (team.picks || []).map((p) => ({
    player: p.player,
    pos: p.pos,
    price: p.paid,
    team: p.team || null,
    sleeperId: p.sleeperId || null,
  }));
}

/**
 * A fetched Sleeper league -> the room shape.
 *
 * @param {object} data the /api/rate-league response: { report, scoring, leagueId, leagueName }
 */
export function sleeperRoomSource(data) {
  const r = data.report;
  const proj = projectRoom(r, data.scoring);
  const lens = data.scoring ? scoringFromSleeper(data.scoring) : null;
  const ranks = lens ? positionRanks(lens) : null;
  // 🔴 THE ROOM decides whether QB heavy is a build available here, not the
  // roster. houseSlotsFromSleeper is the same conversion the seat report uses,
  // so the two paths cannot disagree about what format this league is.
  const qbPremium = isQbPremiumFormat(houseSlotsFromSleeper(r.positions));
  const cards = r.leaderboard.map((t, i) => ({
    key: String(t.rosterId),
    seat: i,
    name: t.name,
    grade: t.grade,
    badge: t.grade,
    badgeIsGrade: true,
    surplus: t.surplus,
    spent: t.spent,
    hasPrices: true,
    picks: t.picks,
    bestBuy: t.bestBuy,
    worstOverpay: t.worstOverpay,
    // benchmarkFormat, not just qbPremium: the label's cut points are quantiles
    // of the real teams in THIS cell, so the build chip and the market numbers
    // on the same card are measured against the same rooms.
    build: buildArchetype({
      picks: t.picks,
      budget: r.budget,
      qbPremium,
      hasPrices: true,
      benchmarkFormat: r.benchmarkFormat,
    }),
    proj: proj ? proj.byTeam[t.rosterId] : null,
  }));

  return {
    source: "sleeper",
    // The testid family this page has always used on the Sleeper path, kept so
    // the DOM contract does not move under the tests that already pin it.
    testPrefix: "team",
    cardTestPrefix: "team-card",
    leagueName: data.leagueName || null,
    teams: r.teams,
    budget: r.budget,
    benchmarkFormat: r.benchmarkFormat,
    format: r.format,
    benchmarkLabel: r.benchmarkLabel,
    nRooms: r.nRooms,
    // The room's own pooled percent, kept alongside the sentences built from
    // it: the size note puts it next to what real rooms of this size pay, and
    // the headline is the difference between the two.
    heat: Number.isFinite(r.roomHeat) ? r.roomHeat : null,
    ...roomClaim(r.benchmarkFormat, r.teams, Number.isFinite(r.roomHeat) ? r.roomHeat : null),
    // Sleeper's own keeper screen, run server-side over the whole room against
    // the benchmark top-60. See the absent-list in RoomReport.jsx.
    keeperFlag: !!r.keeperFlag,
    allPriced: true,
    ranks,
    projTeamCount: proj ? proj.teamCount : 0,
    roomShares: roomMedianShares(r.leaderboard.map((t) => t.picks)),
    // The whole room's best and worst sales, seat by seat. Built from the SAME
    // cards the page draws, so a board can never name a team the leaderboard
    // does not carry.
    ...roomSuperlatives(cards),
    // Why most of the surpluses point one way, and the average each card's own
    // line is shown against. Off the cards, for the same reason.
    ...roomSurplusContext(cards),
    cards,
  };
}

/**
 * Every room-relative number over a hand-entered or pasted room.
 *
 * 🔴 THE PROJECTIONS ARE COPIED BEFORE THEY ARE GRADED. assignPositionGrades
 * mutates what it is handed, and what it would be handed here is the projection
 * object living inside a memoised single-team report — the same object the seat
 * report renders from. Grading it in place would write room words into a report
 * whose whole point is that it is measured against the corpus.
 *
 * Exported for the tests: the function that decides is the function to check.
 */
export function roomView(teams, settings) {
  const byTeam = {};
  teams.forEach((t, i) => {
    byTeam[i] = { ...(t.report.projection || { matched: [], starters: [], perPlayer: {} }) };
  });
  assignPositionGrades(byTeam, projectionSlots(settings.positions));

  // Points rank: week 1, highest first, ties sharing a rank (1, 2, 2, 4).
  const ordered = Object.entries(byTeam).sort((a, b) => (b[1].week1 || 0) - (a[1].week1 || 0));
  ordered.forEach(([, t], i) => {
    const prev = i > 0 ? ordered[i - 1][1] : null;
    t.rank = prev && prev.week1 === t.week1 ? prev.rank : i + 1;
  });

  // 🔴 A MARKET GRADE NEEDS EVERY TEAM PRICED. One unpriced roster would sit at
  // surplus 0 and take a middling grade it did not earn, and worse, it would
  // shift every other team's rung. All or nothing, the same rule one team's own
  // report applies to its own picks.
  const allPriced = teams.length > 0 && teams.every((t) => t.report.hasPrices);
  const grades = allPriced
    ? roomGradesBySurplus(teams.map((t) => t.report.surplus))
    : teams.map(() => null);

  return { byTeam, grades, allPriced };
}

/**
 * A pasted or hand-entered room -> the same room shape.
 *
 * @param {Array}  teams    [{ name, report }] in entry order, report from rateTeam
 * @param {object} settings the league in force
 * @param {string} leagueName what this league is called, if anything
 */
export function localRoomSource(teams, settings, leagueName) {
  const { byTeam, grades, allPriced } = roomView(teams, settings);
  const allPicks = allPriced ? teams.flatMap((t) => t.report.picks) : [];
  const first = teams[0] ? teams[0].report : null;

  // Best team first when the room is graded on money, best projected lineup
  // first when it is not: the order should lead with whatever the badge says.
  //
  // 🔴 THE SAME SORT THE SWITCHER AND THE ROOM STRIP USE. This page was already
  // ranked and those two were not, which is how the A+ came to sit ninth in the
  // switcher above an open seat (roomOrder.js's header carries the report). One
  // sort, three consumers — so a seat cannot be third here and ninth there.
  const order = roomRowOrder(
    teams.map((t, i) => ({ rank: allPriced ? grades[i].rank : null, week1: byTeam[i].week1 }))
  );

  const qbPremium = isQbPremiumFormat(settings.positions);
  const cards = order.map((i) => {
    const rep = teams[i].report;
    return {
      key: teams[i].name,
      // The seat this card came from, so a click-through opens the seat the
      // reader tapped rather than the slot it happened to be ranked into.
      seat: i,
      name: teams[i].name,
      grade: grades[i] ? grades[i].grade : null,
      badge: grades[i] ? grades[i].grade : ordinal(byTeam[i].rank),
      badgeIsGrade: !!grades[i],
      surplus: rep.surplus,
      spent: rep.spent,
      hasPrices: rep.hasPrices,
      picks: rep.picks,
      bestBuy: rep.bestBuy,
      worstOverpay: rep.worstOverpay,
      // Per TEAM, not per room: a room where one seat was typed without dollars
      // still names the builds of the seats that were priced. That is a
      // different rule from the room-wide steal boards above, and deliberately
      // so — a build is a fact about one roster, where a steal board is a
      // ranking across all of them.
      build: buildArchetype({
        picks: rep.picks,
        budget: settings.budget,
        qbPremium,
        hasPrices: rep.hasPrices,
        // Off the seat's OWN report rather than the room's `first`, so a seat
        // typed before the room knew its format still reads the right cell.
        benchmarkFormat: rep.benchmarkFormat,
      }),
      proj: byTeam[i],
    };
  });

  // Null on an unpriced room, and null is what the market claim reads as "there
  // is no market number here at all" — the same absent-not-defaulted rule the
  // rest of this module runs on.
  const localHeat = allPriced ? roomHeatOf(allPicks) : null;

  return {
    source: "local",
    // The testid family the hand-entered room has always used.
    testPrefix: "room",
    cardTestPrefix: "room-team",
    leagueName: leagueName || null,
    teams: settings.teams,
    budget: settings.budget,
    benchmarkFormat: first ? first.benchmarkFormat : null,
    format: first ? first.format : null,
    benchmarkLabel: first ? first.benchmarkLabel : null,
    nRooms: first ? first.nRooms : null,
    // 🔴 THE SAME SENTENCE THE FETCHED ROOM GETS, FROM THE SAME ESTIMATOR — and
    // the room-relative one when there is no market to compare against, which
    // is the case both for a roster typed without dollars and for a room whose
    // SIZE the corpus cannot yet back a percentage for.
    heat: localHeat,
    ...roomClaim(first ? first.benchmarkFormat : null, settings.teams, localHeat),
    // 🔴 NOT COMPUTED ON THIS PATH. See RoomReport.jsx's absent-list: the keeper
    // screen is a whole-room pass that only the server report runs, and a false
    // here is "nobody looked", never "this room has no keepers". The page draws
    // nothing either way, which is the honest rendering of both.
    keeperFlag: false,
    allPriced,
    // One board for the whole room: every team here is scored under one
    // league's scoring, so there is one position board and it comes off any of
    // them.
    ranks: first ? first.posRanks : null,
    projTeamCount: teams.length,
    roomShares: roomMedianShares(teams.map((t) => t.report.picks)),
    // 🔴 GATED ON allPriced. A room where one roster was typed without dollars
    // has no room-wide market comparison to rank, and a board built from the
    // priced half would be "the five best buys among the teams that happened to
    // carry prices".
    ...roomSuperlatives(allPriced ? cards : []),
    // Same call, same cards. An unpriced room reaches it too and comes back with
    // two nulls, which is the honest answer rather than a special case here.
    ...roomSurplusContext(cards),
    cards,
  };
}
